import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  setPersistence,
  browserSessionPersistence,
} from 'firebase/auth';
import {
  auth,
  resolveRoleFromEmail,
  ROLE_ADMIN,
  ROLE_QUALITY_MANAGER,
} from '../config/firebase';

const AuthContext = createContext({
  user: null,
  role: null,
  loading: true,
  isAuthenticated: false,
  login: async () => {},
  logout: async () => {},
});

function persistSession(email, role) {
  localStorage.setItem(
    'user',
    JSON.stringify({
      email,
      role,
      full_name: role === ROLE_ADMIN ? 'Admin' : 'Quality Manager',
    })
  );
  localStorage.setItem('isAuthenticated', 'true');
}

function clearSession() {
  localStorage.removeItem('user');
  localStorage.removeItem('isAuthenticated');
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribe = () => {};

    const initAuth = async () => {
      try {
        // Session-only: closing the browser requires login again on next visit.
        await setPersistence(auth, browserSessionPersistence);
      } catch (err) {
        console.error('Failed to set Firebase auth persistence:', err);
      }

      // Clear any legacy backend-login flags left in localStorage.
      if (!auth.currentUser) {
        clearSession();
      }

      unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
        if (firebaseUser) {
          const resolvedRole = resolveRoleFromEmail(firebaseUser.email);
          if (!resolvedRole) {
            firebaseSignOut(auth).catch(() => {});
            clearSession();
            setUser(null);
            setRole(null);
          } else {
            setUser(firebaseUser);
            setRole(resolvedRole);
            persistSession(firebaseUser.email, resolvedRole);
          }
        } else {
          setUser(null);
          setRole(null);
          clearSession();
        }
        setLoading(false);
      });
    };

    initAuth();

    return () => unsubscribe();
  }, []);

  const login = async (email, password, selectedRole) => {
    const credential = await signInWithEmailAndPassword(auth, email, password);
    const resolvedRole = resolveRoleFromEmail(credential.user.email);

    if (!resolvedRole) {
      await firebaseSignOut(auth);
      clearSession();
      const err = new Error('This email is not authorized for this application.');
      err.code = 'auth/unauthorized-email';
      throw err;
    }

    if (resolvedRole !== selectedRole) {
      await firebaseSignOut(auth);
      clearSession();
      const err = new Error(
        selectedRole === ROLE_ADMIN
          ? 'This account is not an Admin. Select Quality Manager and try again.'
          : 'This account is not a Quality Manager. Select Admin and try again.'
      );
      err.code = 'auth/role-mismatch';
      throw err;
    }

    persistSession(credential.user.email, resolvedRole);
    setUser(credential.user);
    setRole(resolvedRole);
    return { user: credential.user, role: resolvedRole };
  };

  const logout = async () => {
    await firebaseSignOut(auth);
    clearSession();
    setUser(null);
    setRole(null);
  };

  const isAuthenticated = Boolean(user && role);

  return (
    <AuthContext.Provider value={{ user, role, loading, isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export { ROLE_ADMIN, ROLE_QUALITY_MANAGER };
